import { getToken, refreshAfterUnauthorized, signInAgain } from "../services/auth";
import { navigateToPaymentRequired, navigateToPermissionDenied, navigateToServerError } from "../services/navigation";
import { captureException } from "../services/sentry";

export class QuotaExceededError extends Error {
  readonly isQuotaExceeded = true;
  readonly status = 403;
  constructor(message: string) {
    super(message);
    this.name = "QuotaExceededError";
  }
}

export class PaymentRequiredError extends Error {
  readonly status = 402;
  constructor(message = "Payment required") {
    super(message);
    this.name = "PaymentRequiredError";
  }
}

const API_BASE_URL = process.env.REACT_APP_API_URL;

const HTML_ENTITY_MAP: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  "#39": "'",
};

/*
 * Why responses are entity-decoded: the API stores some user-entered text HTML-escaped (e.g. an operation name
 * "Transport (20m <)" comes back as "Transport (20m &lt;)"), and React would print the entities literally
 * (a20ea2e2, operation history). React escapes text on render, so decoded strings are safe in JSX. They are NOT safe
 * in any HTML sink: never pass response data to innerHTML, dangerouslySetInnerHTML, document.write or an HTML template
 * string without escaping it first (see utils/escapeHtml and components/Moodle/LoadingPage).
 */
const decodeHtmlEntities = (value: string): string =>
  value.replace(/&(#39|amp|lt|gt|quot);/g, (match, entity: string) => HTML_ENTITY_MAP[entity] ?? match);

const decodeResponseEntities = (value: unknown): unknown => {
  if (typeof value === "string") return decodeHtmlEntities(value);
  if (Array.isArray(value)) return value.map(decodeResponseEntities);
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, decodeResponseEntities(item)]));
  }
  return value;
};

/**
 * Original request function - kept for backward compatibility
 * @param url - API endpoint path
 * @param method - HTTP method (default: "GET")
 * @param body - Request body (object or FormData)
 */
const request = async (url: string, method: string = "GET", body?: object | FormData): Promise<any> =>
  send(url, method, body, false);

const send = async (
  url: string,
  method: string,
  body: object | FormData | undefined,
  isRetry: boolean,
): Promise<any> => {
  const u = `${API_BASE_URL}/v1/${url}`.replace(/([^:])(\/\/)/g, "$1/");
  const token = await getToken();

  const options: RequestInit = {
    method,
    headers: {
      authorization: `Bearer ${token}`,
    },
  };

  if (body) {
    if (body instanceof FormData) {
      options.body = body;
    } else {
      (options.headers as Record<string, string>)["Content-Type"] = "application/json";
      options.body = JSON.stringify(body);
    }
  }

  let response: Response;
  try {
    response = await fetch(u, options);
  } catch (e) {
    // The request never reached the server (offline, DNS, connection refused,
    // CORS) — there's no server-side log for this, so report it from the client.
    captureException(e, { url: u, method });
    throw e;
  }

  if (response.status < 300) {
    const text = await response.text();
    // Empty body on a 2xx (common for POST/PUT/DELETE) is normal, not an error.
    if (text.trim() === "") {
      return {};
    }
    try {
      return decodeResponseEntities(JSON.parse(text));
    } catch (e) {
      // 2xx with a non-empty body that won't parse — the server considers this
      // a success, so it won't be logged there. Report and keep the {} fallback.
      captureException(e, { url: u, method, status: response.status, bodyPreview: text.slice(0, 200) });
      return {};
    }
  } else {
    if (response.status === 400 || response.status === 409) {
      throw response;
    }
    if (response.status === 401) {
      // One token refresh and retry (another request may already have refreshed it); a second 401 means a fresh
      // token did not help either: sign in again through the loop guard. Never a global SSO logout.
      if (!isRetry) {
        await refreshAfterUnauthorized(token);
        return send(url, method, body, true);
      }
      signInAgain("api_unauthorized");
    } else if (response.status === 402) {
      const errorData = await response.json();
      if (errorData.message?.includes("not purchased course")) {
        navigateToPaymentRequired("course");
        // Throw a special error that can be caught but won't show a dialog
        throw new PaymentRequiredError();
      } else {
        navigateToPaymentRequired();
      }
    } else if (response.status === 403) {
      const errorData = await response.json().catch(() => ({}));
      if (errorData.message && errorData.message.startsWith("Company course limit reached")) {
        throw new QuotaExceededError(errorData.message);
      }
      navigateToPermissionDenied();
      return false;
    } else if (response.status === 504) {
      const error: any = new Error("Request timed out");
      error.status = 504;
      error.isTimeout = true;
      throw error;
    } else if (response.status >= 500) {
      navigateToServerError();
    }
    throw response.status;
  }
};

request.post = async (url: string, body?: object | FormData): Promise<any> => request(url, "POST", body);
request.put = async (url: string, body?: object | FormData): Promise<any> => request(url, "PUT", body);
request.delete = async (url: string): Promise<any> => request(url, "DELETE");

export default request;
